
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { CraftCaseStudies } from '../CraftCaseStudies';
import {
  Factory, PenTool, HardHat, CheckCircle2,
  Wrench, Minus, Plus, Cpu, Calculator, ArrowRight, X, Check, Loader2, Boxes, Lock, Server, MessageSquare, Zap
} from 'lucide-react';
import { Page } from '../../types';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';

const ProductionTerminal = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep((prev) => (prev + 1) % 4), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-[#0F172A] mt-2 h-[320px] flex flex-col">
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="ml-4 text-xs font-mono text-gray-400">{t('craft.terminal.log')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
          <span className="text-[10px] font-mono text-amber-500 uppercase">{t('craft.terminal.status_active')}</span>
        </div>
      </div>
      <div className="p-6 font-mono text-xs md:text-sm leading-loose overflow-hidden relative flex-1 text-amber-100">
        <div className="text-gray-500">[07:00:01] {t('craft.terminal.scan_project')}</div>
        <div className={`transition-opacity duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-amber-400">[07:00:05] {t('craft.terminal.entities_detected')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-blue-400 mt-2">[07:00:06] {t('craft.terminal.calculating_demand')}</div>
        </div>
        <div className={`transition-opacity duration-500 mt-4 ${step >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-amber-900/20 p-3 rounded border border-amber-500/20 text-amber-300">{t('craft.terminal.action')}</div>
        </div>
      </div>
    </div>
  );
};

export const CraftPage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const subSectors = [
    { title: t('craft.sectors.items.01.title'), id: "01", desc: t('craft.sectors.items.01.desc'), highlight: t('craft.sectors.items.01.highlight') },
    { title: t('craft.sectors.items.02.title'), id: "02", desc: t('craft.sectors.items.02.desc'), highlight: t('craft.sectors.items.02.highlight') },
    { title: t('craft.sectors.items.03.title'), id: "03", desc: t('craft.sectors.items.03.desc'), highlight: t('craft.sectors.items.03.highlight') },
    { title: t('craft.sectors.items.04.title'), id: "04", desc: t('craft.sectors.items.04.desc'), highlight: t('craft.sectors.items.04.highlight') },
    { title: t('craft.sectors.items.05.title'), id: "05", desc: t('craft.sectors.items.05.desc'), highlight: t('craft.sectors.items.05.highlight') },
    { title: t('craft.sectors.items.06.title'), id: "06", desc: t('craft.sectors.items.06.desc'), highlight: t('craft.sectors.items.06.highlight') },
    { title: t('craft.sectors.items.07.title'), id: "07", desc: t('craft.sectors.items.07.desc'), highlight: t('craft.sectors.items.07.highlight') },
    { title: t('craft.sectors.items.08.title'), id: "08", desc: t('craft.sectors.items.08.desc'), highlight: t('craft.sectors.items.08.highlight'), variant: 'dark' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="pt-4">
              <div className="inline-block px-4 py-2 bg-amber-50 text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-amber-100">{t('craft.hero.tagline')}</div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">
                {t('craft.hero.title_line1')} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-500">{t('craft.hero.title_line2')}</span>
              </h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-lg">{t('craft.hero.subtitle')}</p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-amber-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-amber-700 transition-all shadow-lg rounded-sm">{t('craft.hero.cta_strategy')}</button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}><ProductionTerminal /></Reveal>
        </div>
      </section>

      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('craft.sectors.title').split(' ')[0]}<br />{t('craft.sectors.title').split(' ')[1]}
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
            {subSectors.map((sector, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div
                  onClick={() => setIsModalOpen(true)}
                  className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative cursor-pointer overflow-hidden h-full ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'bg-white hover:bg-amber-50/30'}`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-amber-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                  <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                  <div>
                    <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                    <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-40 bg-[#0B1121] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-amber-900/30 text-amber-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-amber-500/30">{t('craft.consultant.tagline')}</div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('craft.consultant.title')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t('craft.consultant.subtitle')}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}><SmartConsultant context="craft" title={t('craft.consultant.bot_title')} subtitle={t('craft.consultant.bot_subtitle')} suggestions={t('craft.consultant.suggestions', { returnObjects: true }) as string[]} /></Reveal>
        </div>
      </section>

      <CraftCaseStudies />

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Craft" />
    </div>
  );
};
